/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { IntegrationSettings } from '@shared/types/user_settings';
import { ProductAppSettings, JiraConfig, GitHubConfig, GitLabConfig, LinearConfig, CodingAgent, SourceControlProvider } from '../types';
import { GitHubService } from '../services/githubService';
import { GitLabService } from '../services/gitlabService';
import { DashboardSettings } from '@shared/types/dashboard';
import { SkillShopService } from '../services/skillShopService';
import { normalizeRepoUrl } from '../utils/repoPaths';
import {
  ensureProductRepos,
  type ProductForEnsureRepos,
  type EnsureReposResult,
} from '../lib/api';
import type { Product } from '@shared/types/products';

// Quick request field state for persistence across navigation
interface QuickRequestState {
  issues: string;
  userStories: string;
  releases: string;
  products: string;
}

interface AppContextType {
  jiraConfig: JiraConfig | null;
  isConfigured: boolean;
  updateJiraConfig: (config: JiraConfig) => void;
  clearJiraConfig: () => void;
  githubConfig: GitHubConfig | null;
  githubService: GitHubService | null;
  isGitHubConfigured: boolean;
  updateGitHubConfig: (config: GitHubConfig) => void;
  clearGitHubConfig: () => void;
  gitlabConfig: GitLabConfig | null;
  gitlabService: GitLabService | null;
  isGitLabConfigured: boolean;
  updateGitLabConfig: (config: GitLabConfig) => void;
  clearGitLabConfig: () => void;
  sourceControlProvider: SourceControlProvider;
  updateSourceControlProvider: (provider: SourceControlProvider) => void;
  linearConfig: LinearConfig | null;
  isLinearConfigured: boolean;
  updateLinearConfig: (config: LinearConfig) => void;
  clearLinearConfig: () => void;
  codingAgent: CodingAgent;
  updateCodingAgent: (agent: CodingAgent) => void;
  dashboardSettings: DashboardSettings;
  updateDashboardSettings: (settings: DashboardSettings) => void;
  activeRepoUrl: string | null;
  activeProduct: Product | null;
  isLoadingProduct: boolean;
  setActiveProduct: (product: Product | null) => void;
  setActiveRepoUrl: (repoUrl: string | null) => void;
  // Quick request field values (persisted in memory across navigation)
  quickRequestState: QuickRequestState;
  setQuickRequest: (page: keyof QuickRequestState, value: string) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const skillShopService = useMemo(() => new SkillShopService(), []);

  // Core state
  const [jiraConfig, setJiraConfig] = useState<JiraConfig | null>(null);
  const [githubConfig, setGitHubConfig] = useState<GitHubConfig | null>(null);
  const [githubService, setGitHubService] = useState<GitHubService | null>(null);
  const [gitlabConfig, setGitLabConfig] = useState<GitLabConfig | null>(null);
  const [gitlabService, setGitLabService] = useState<GitLabService | null>(null);
  const [sourceControlProvider, setSourceControlProvider] = useState<SourceControlProvider>('github');
  const [linearConfig, setLinearConfig] = useState<LinearConfig | null>(null);
  const [codingAgent, setCodingAgent] = useState<CodingAgent>('claude-code');
  const [dashboardSettings, setDashboardSettings] = useState<DashboardSettings>({
    activityDays: 7,
    velocityWeeks: 4,
  });
  const [activeRepoUrl, setActiveRepoUrlState] = useState<string | null>(null);
  const [activeProduct, setActiveProductState] = useState<Product | null>(null);
  const [activeProductId, setActiveProductIdState] = useState<string | null>(null);
  const [isLoadingProductData, setIsLoadingProductData] = useState(false);
  const [isRepoSyncing, setIsRepoSyncing] = useState(false);
  
  // Quick request field state (persisted in memory across navigation)
  const [quickRequestState, setQuickRequestState] = useState<QuickRequestState>({
    issues: '',
    userStories: '',
    releases: '',
    products: '',
  });

  // Combine loading states: product is considered "loading" until both
  // the product data is fetched AND the repo sync is complete (or not needed)
  const isLoadingProduct = isLoadingProductData || isRepoSyncing;

  // Track initialization
  const isInitialized = useRef(false);
  const isSaving = useRef(false);
  const lastNotifiedRepoUrl = useRef<string | null>(null);
  const lastEnsuredRepoUrl = useRef<string | null>(null);

  // Load settings from SkillShop on mount
  useEffect(() => {
    let cancelled = false;

    // Skip if already initialized
    if (isInitialized.current) {
      return;
    }

    const loadSettings = async () => {
      try {
        console.log('[AppContext] Loading user settings from SkillShop...');
        const userSettings = await skillShopService.getUserSettings();
        
        console.log('[AppContext] getUserSettings returned:', userSettings);
        
        if (cancelled) {
          console.log('[AppContext] Settings load cancelled');
          return;
        }
        
        if (!userSettings) {
          console.log('[AppContext] No settings found, userSettings is null/undefined');
          return;
        }

        const productApp = userSettings.integrationSettings?.productApp;
        console.log('[AppContext] Loaded product app settings:', productApp);

        // Load JIRA config
        if (productApp?.jiraConfig) {
          console.log('[AppContext] Setting JIRA config');
          setJiraConfig(productApp.jiraConfig);
        }

        // Load GitHub config
        if (productApp?.githubConfig) {
          console.log('[AppContext] Setting GitHub config');
          setGitHubConfig(productApp.githubConfig);
          try {
            setGitHubService(new GitHubService(productApp.githubConfig));
          } catch (error) {
            console.error('[AppContext] Failed to create GitHub service:', error);
          }
        }

        // Load GitLab config
        if (productApp?.gitlabConfig) {
          console.log('[AppContext] Setting GitLab config');
          setGitLabConfig(productApp.gitlabConfig);
          try {
            setGitLabService(new GitLabService(productApp.gitlabConfig));
          } catch (error) {
            console.error('[AppContext] Failed to create GitLab service:', error);
          }
        }

        // Load source control provider
        if (productApp?.sourceControlProvider) {
          setSourceControlProvider(productApp.sourceControlProvider);
        }

        // Load Linear config
        if (productApp?.linearConfig) {
          console.log('[AppContext] Setting Linear config');
          setLinearConfig(productApp.linearConfig);
        }

        // Load other settings
        if (productApp?.codingAgent) {
          setCodingAgent(productApp.codingAgent);
        }

        if (productApp?.dashboardSettings) {
          setDashboardSettings(productApp.dashboardSettings);
        }

        // Only load by product ID - this is the single source of truth
        // The repo URL will be derived from the loaded product
        if (productApp?.activeProductId) {
          setActiveProductIdState(productApp.activeProductId);
          setIsLoadingProductData(true);
        }

        console.log('[AppContext] Settings loaded successfully');
        
        // Mark as initialized only after successful load
        isInitialized.current = true;
      } catch (error) {
        if (!cancelled) {
          console.error('[AppContext] Failed to load settings:', error);
        }
      }
    };

    loadSettings();

    return () => {
      cancelled = true;
    };
  }, [skillShopService]);

  // Notify MCP filesystem server about roots changes
  const notifyMcpRootsChanged = useCallback(async (product: Product) => {
    if (!product.repositoryUrl) {
      console.log('[AppContext] Product has no repository URL, skipping MCP notification');
      return;
    }

    // Use SkillShopService to send the notification
    const result = await skillShopService.notifyMcpRootsChanged({
      title: product.title,
      repositoryUrl: product.repositoryUrl,
      repoPath: product.repoPath
    });

    if (result.success) {
      lastNotifiedRepoUrl.current = product.repositoryUrl;
    }
  }, [skillShopService]);

  // Notify MCP Vector Search server about product changes (for Electron app)
  // This triggers registration and indexing of the product's repository
  const notifyVectorSearchProductChanged = useCallback(async (product: Product) => {
    if (!product.repositoryUrl) {
      console.log('[AppContext] Product has no repository URL, skipping vector search notification');
      return;
    }

    // Use SkillShopService to notify vector search
    // This is async and non-blocking - we don't wait for indexing to complete
    skillShopService.notifyVectorSearchProductChanged({
      title: product.title,
      repositoryUrl: product.repositoryUrl,
      repoPath: product.repoPath
    }).then(result => {
      if (result.success) {
        console.log('[AppContext] Vector search notified successfully:', result.repoId);
      } else {
        // Don't fail - vector search is optional enhancement
        console.warn('[AppContext] Vector search notification failed:', result.error);
      }
    }).catch(error => {
      // Don't fail - vector search is optional enhancement
      console.warn('[AppContext] Vector search notification error:', error);
    });
  }, [skillShopService]);

  // Load active product by ID
  useEffect(() => {
    // No product ID to load
    if (!activeProductId) {
      return;
    }

    // Product is already loaded - ensure isLoadingProductData is false
    // This handles the case where isLoadingProductData was set to true during settings load
    // but the product was already loaded (e.g., due to React Strict Mode's simulated remount)
    if (activeProduct?.id === activeProductId) {
      setIsLoadingProductData(false);
      return;
    }

    let cancelled = false;
    setIsLoadingProductData(true);

    const loadProduct = async () => {
      try {
        const product = await skillShopService.getProduct(activeProductId);
        if (cancelled) return;

        if (product) {
          setActiveProductState(product);
          const normalized = product.repositoryUrl ? normalizeRepoUrl(product.repositoryUrl) : null;
          setActiveRepoUrlState(normalized);
          // If product has a repo URL, repo sync will run and set isRepoSyncing
          // If no repo URL, we're done loading
          if (!product.repositoryUrl) {
            setIsLoadingProductData(false);
          }
        } else {
          // Product was deleted or doesn't exist - clear the active product
          console.warn('[AppContext] Product not found for ID:', activeProductId, '- clearing active product');
          setActiveProductState(null);
          setActiveProductIdState(null);
          setIsLoadingProductData(false);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[AppContext] Failed to load active product:', error);
          setIsLoadingProductData(false);
        }
      }
    };

    loadProduct();

    return () => {
      cancelled = true;
    };
  }, [activeProductId, activeProduct, skillShopService]);

  // Ensure active product's repository exists and is synced when product changes
  // This handles both app startup (loading from settings) and manual product selection
  useEffect(() => {
    if (!activeProduct?.repositoryUrl) {
      // No repo to sync - clear product loading state if it was set
      setIsLoadingProductData(false);
      setIsRepoSyncing(false);
      return;
    }

    // Only ensure if the repo URL has changed since last ensure
    if (lastEnsuredRepoUrl.current === activeProduct.repositoryUrl) {
      // Already synced this repo - ensure loading states are cleared
      setIsLoadingProductData(false);
      setIsRepoSyncing(false);
      return;
    }

    // Mark this repo as being ensured and set syncing state
    lastEnsuredRepoUrl.current = activeProduct.repositoryUrl;
    setIsRepoSyncing(true);

    const ensureAndSyncRepo = async () => {
      // Set a timeout to prevent indefinite hangs (30 seconds max)
      const SYNC_TIMEOUT_MS = 30000;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let isTimedOut = false;

      const timeoutPromise = new Promise<void>((_, reject) => {
        timeoutId = setTimeout(() => {
          isTimedOut = true;
          reject(new Error('Repository sync timed out after 30 seconds'));
        }, SYNC_TIMEOUT_MS);
      });

      try {
        await Promise.race([
          (async () => {
            // Step 1: Ensure the repository exists (clone if needed)
            const productData: ProductForEnsureRepos = {
              id: activeProduct.id,
              productId: activeProduct.productId,
              title: activeProduct.title,
              description: activeProduct.description,
              repositoryUrl: activeProduct.repositoryUrl,
              repoPath: activeProduct.repoPath,
            };
            
            const ensureResult: EnsureReposResult = await ensureProductRepos(
              [productData],
              null, // No access token available in context during startup
              sourceControlProvider
            );

            if (ensureResult.success) {
              console.log('[AppContext] Repository ensured on startup:', ensureResult.results?.[0]?.action);
            } else {
              console.error('[AppContext] Failed to ensure repo:', ensureResult.error);
              return;
            }

            // Note: We do NOT sync on startup anymore.
            // The reset-to-main strategy was wiping out local velocity branch commits
            // (which is incorrect for velocity-mode where velocity is the working branch).
            // The auto-sync timer handles periodic syncing based on the configured sync mode.
            // Users can manually sync via the Changes page if needed.
          })(),
          timeoutPromise
        ]);
      } catch (error) {
        if (isTimedOut) {
          console.warn('[AppContext] Repository sync timed out - proceeding with potentially stale data');
        } else {
          console.error('[AppContext] Failed to ensure/sync repo on startup:', error);
        }
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        // Always clear loading states when done (success, failure, or timeout)
        setIsRepoSyncing(false);
        setIsLoadingProductData(false);
      }
    };

    // Run async but track completion via isRepoSyncing state
    ensureAndSyncRepo();
  }, [activeProduct, sourceControlProvider]);

  // Notify MCP servers when active product changes
  useEffect(() => {
    if (!activeProduct?.repositoryUrl) {
      return;
    }

    // Only notify if the repo URL has changed since last notification
    if (lastNotifiedRepoUrl.current === activeProduct.repositoryUrl) {
      return;
    }

    // Send notifications asynchronously (don't block UI)
    // 1. Notify filesystem MCP server about roots change
    notifyMcpRootsChanged(activeProduct);
    
    // 2. Notify vector search server to register/index the repository
    // This enables semantic code search for the product
    notifyVectorSearchProductChanged(activeProduct);
  }, [activeProduct, notifyMcpRootsChanged, notifyVectorSearchProductChanged]);


  // Save settings to SkillShop
  const saveSettings = useCallback(
    async (updates: Partial<ProductAppSettings>) => {
      // Prevent concurrent saves
      if (isSaving.current) {
        console.log('[AppContext] Save already in progress, skipping');
        return;
      }

      isSaving.current = true;

      try {
        console.log('[AppContext] Saving settings:', updates);

        // Get current settings from SkillShop to ensure we have the latest
        const userSettings = await skillShopService.getUserSettings();
        const currentProductApp = userSettings?.integrationSettings?.productApp || {};

        // Merge updates with current settings
        const updatedProductApp: ProductAppSettings = {
          ...currentProductApp,
          ...updates,
          updatedAt: new Date().toISOString(),
        };

        console.log('[AppContext] Merged settings:', updatedProductApp);

        // Build integration settings
        const integrationSettings: IntegrationSettings = {
          ...(userSettings?.integrationSettings || {}),
          productApp: updatedProductApp,
        };

        // Save to SkillShop
        await skillShopService.updateIntegrationSettings(integrationSettings);
        console.log('[AppContext] Settings saved successfully');
      } catch (error) {
        console.error('[AppContext] Failed to save settings:', error);
      } finally {
        isSaving.current = false;
      }
    },
    [skillShopService],
  );

  // Update handlers
  const updateJiraConfig = useCallback(
    (config: JiraConfig) => {
      console.log('[AppContext] Updating JIRA config');
      setJiraConfig(config);
      saveSettings({ jiraConfig: config });
    },
    [saveSettings],
  );

  const clearJiraConfig = useCallback(() => {
    console.log('[AppContext] Clearing JIRA config');
    setJiraConfig(null);
    saveSettings({ jiraConfig: null });
  }, [saveSettings]);

  const updateGitHubConfig = useCallback(
    (config: GitHubConfig) => {
      console.log('[AppContext] Updating GitHub config');
      setGitHubConfig(config);
      setSourceControlProvider('github');
      try {
        setGitHubService(new GitHubService(config));
      } catch (error) {
        console.error('[AppContext] Failed to create GitHub service:', error);
        setGitHubService(null);
      }
      saveSettings({ githubConfig: config, sourceControlProvider: 'github' });
    },
    [saveSettings],
  );

  const clearGitHubConfig = useCallback(() => {
    console.log('[AppContext] Clearing GitHub config');
    setGitHubConfig(null);
    setGitHubService(null);
    saveSettings({ githubConfig: null });
  }, [saveSettings]);

  const updateGitLabConfig = useCallback(
    (config: GitLabConfig) => {
      console.log('[AppContext] Updating GitLab config');
      setGitLabConfig(config);
      setSourceControlProvider('gitlab');
      try {
        setGitLabService(new GitLabService(config));
      } catch (error) {
        console.error('[AppContext] Failed to create GitLab service:', error);
        setGitLabService(null);
      }
      saveSettings({ gitlabConfig: config, sourceControlProvider: 'gitlab' });
    },
    [saveSettings],
  );

  const clearGitLabConfig = useCallback(() => {
    console.log('[AppContext] Clearing GitLab config');
    setGitLabConfig(null);
    setGitLabService(null);
    saveSettings({ gitlabConfig: null });
  }, [saveSettings]);

  const updateSourceControlProvider = useCallback(
    (provider: SourceControlProvider) => {
      console.log('[AppContext] Updating source control provider:', provider);
      setSourceControlProvider(provider);
      saveSettings({ sourceControlProvider: provider });
    },
    [saveSettings],
  );

  const updateLinearConfig = useCallback(
    (config: LinearConfig) => {
      console.log('[AppContext] Updating Linear config');
      setLinearConfig(config);
      saveSettings({ linearConfig: config });
    },
    [saveSettings],
  );

  const clearLinearConfig = useCallback(() => {
    console.log('[AppContext] Clearing Linear config');
    setLinearConfig(null);
    saveSettings({ linearConfig: null });
  }, [saveSettings]);

  const updateCodingAgent = useCallback(
    (agent: CodingAgent) => {
      console.log('[AppContext] Updating coding agent:', agent);
      setCodingAgent(agent);
      saveSettings({ codingAgent: agent });
    },
    [saveSettings],
  );

  const updateDashboardSettings = useCallback(
    (settings: DashboardSettings) => {
      console.log('[AppContext] Updating dashboard settings:', settings);
      setDashboardSettings(settings);
      saveSettings({ dashboardSettings: settings });
    },
    [saveSettings],
  );

  const setActiveProduct = useCallback(
    (product: Product | null) => {
      console.log('[AppContext] Setting active product:', product?.id);
      const normalized = product?.repositoryUrl ? normalizeRepoUrl(product.repositoryUrl) : null;
      setActiveProductState(product);
      setActiveProductIdState(product?.id ?? null);
      setActiveRepoUrlState(normalized);

      // Only save the product ID - the repo URL will be derived when loading
      saveSettings({
        activeProductId: product?.id ?? null,
      });

      // Reset lastEnsuredRepoUrl to trigger ensure + sync via the useEffect
      // when switching products (even to the same product)
      if (product?.repositoryUrl && lastEnsuredRepoUrl.current !== product.repositoryUrl) {
        // New product selected - useEffect will handle ensure + sync
        // Nothing to do here
      } else if (product?.repositoryUrl) {
        // Same product re-selected - reset the ref to allow re-sync
        lastEnsuredRepoUrl.current = null;
      }
    },
    [saveSettings],
  );

  const setActiveRepoUrl = useCallback(
    (repoUrl: string | null) => {
      console.log('[AppContext] Setting active repo URL:', repoUrl);
      // In the simplified model, we don't support setting repo URL directly
      // If repoUrl is null, clear the active product
      // If repoUrl is set, this is a no-op (caller should use setActiveProduct instead)
      if (!repoUrl) {
        setActiveProductState(null);
        setActiveProductIdState(null);
        setActiveRepoUrlState(null);
        saveSettings({
          activeProductId: null,
        });
      }
      // If repoUrl is provided but doesn't match current product, do nothing
      // The caller should load the product and use setActiveProduct instead
    },
    [saveSettings],
  );

  const setQuickRequest = useCallback(
    (page: keyof QuickRequestState, value: string) => {
      setQuickRequestState(prev => ({
        ...prev,
        [page]: value,
      }));
    },
    [],
  );

  // Computed values
  const isConfigured = !!(jiraConfig && jiraConfig.url && jiraConfig.username && jiraConfig.apiToken);
  const isGitHubConfigured = !!(githubConfig && githubConfig.accessToken);
  const isGitLabConfigured = !!(gitlabConfig && gitlabConfig.accessToken);
  const isLinearConfigured = !!(linearConfig && linearConfig.apiKey);

  return (
    <AppContext.Provider
      value={{
        jiraConfig,
        isConfigured,
        updateJiraConfig,
        clearJiraConfig,
        githubConfig,
        githubService,
        isGitHubConfigured,
        updateGitHubConfig,
        clearGitHubConfig,
        gitlabConfig,
        gitlabService,
        isGitLabConfigured,
        updateGitLabConfig,
        clearGitLabConfig,
        sourceControlProvider,
        updateSourceControlProvider,
        linearConfig,
        isLinearConfigured,
        updateLinearConfig,
        clearLinearConfig,
        codingAgent,
        updateCodingAgent,
        dashboardSettings,
        updateDashboardSettings,
        activeRepoUrl,
        activeProduct,
        isLoadingProduct,
        setActiveProduct,
        setActiveRepoUrl,
        quickRequestState,
        setQuickRequest,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
